import ModalCloseButton from '../../../../Component/UI/ModalCloseButton';
import { ReactNode, useEffect, useId, useRef } from "react";
import styled from "styled-components";
import { fontStyles } from "../../../../Component/style/admin_font";

type LiveDetailDialogProps = {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  size?: "compact" | "medium" | "large" | "wide";
  width?: number;
  height?: number;
  scrollBody?: boolean;
  closeOnlyByButton?: boolean;
  suspended?: boolean;
  keepMounted?: boolean;
};

const focusableSelector = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");
const hiddenContainerSelector = '[hidden], [aria-hidden="true"], [style*="display: none"]';

const LiveDetailDialog = ({
  open,
  title,
  description,
  children,
  footer,
  onClose,
  size = "medium",
  width,
  height,
  scrollBody = true,
  closeOnlyByButton = false,
  suspended = false,
  keepMounted = false,
}: LiveDetailDialogProps) => {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open || suspended) return;

    const owner = dialogRef.current?.ownerDocument ?? document;
    const view = owner.defaultView ?? window;
    returnFocusRef.current = owner.activeElement as HTMLElement | null;
    const previousOverflow = owner.body.style.overflow;
    owner.body.style.overflow = "hidden";

    const frame = view.requestAnimationFrame(() => {
      const firstControl = dialogRef.current?.querySelector<HTMLElement>(focusableSelector);
      (firstControl || dialogRef.current)?.focus();
    });

    return () => {
      view.cancelAnimationFrame(frame);
      owner.body.style.overflow = previousOverflow;
      returnFocusRef.current?.focus();
    };
  }, [open, suspended]);

  // Updating close handlers while typing must not rerun initial focus or scroll locking.
  useEffect(() => {
    if (!open || suspended) return;
    const owner = dialogRef.current?.ownerDocument ?? document;

    const handleKeyDown = (event: KeyboardEvent) => {
      // The section dialog yields keyboard control to its open editing dialog.
      if (keepMounted && Array.from(dialogRef.current?.querySelectorAll('[role="dialog"], [role="alertdialog"]') || [])
        .some(dialog => !dialog.closest(hiddenContainerSelector))) return;
      if (event.key === "Escape") {
        event.preventDefault();
        if (!closeOnlyByButton) onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;
      const controls = [...Array.from(dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector)),
        ...Array.from(owner.querySelectorAll<HTMLElement>('[data-live-feedback] button:not([disabled])'))]
        .filter(control => !keepMounted || !control.closest(hiddenContainerSelector));
      if (controls.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && owner.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && owner.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    owner.addEventListener("keydown", handleKeyDown);
    return () => {
      owner.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, closeOnlyByButton, suspended, keepMounted]);

  if (!open && !keepMounted) return null;

  return (
    <Overlay data-admin-modal-overlay="" hidden={!open} style={suspended ? { visibility: 'hidden' } : undefined} aria-hidden={!open || suspended || undefined} onMouseDown={event => !suspended && !closeOnlyByButton && event.target === event.currentTarget && onClose()}>
      <Dialog data-admin-modal=""
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        $size={size}
        $width={width}
        $height={height}
      >
        <Header data-admin-modal-header="">
          <HeadingGroup>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
          </HeadingGroup>
          <CloseButton data-live-icon-control tone="admin" type="button" onClick={onClose} aria-label={`${title} 창 닫기`} />
        </Header>
        <Body data-admin-modal-body={scrollBody ? "" : "contained"} $scroll={scrollBody}>{children}</Body>
        {footer && <Footer data-admin-modal-footer="">{footer}</Footer>}
      </Dialog>
    </Overlay>
  );
};

export default LiveDetailDialog;

const Overlay = styled.div`
  &[hidden] { display: none; }
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: rgba(20, 16, 33, 0.56);
  backdrop-filter: blur(3px);
`;

const Dialog = styled.div<{ $size: "compact" | "medium" | "large" | "wide"; $width?: number; $height?: number }>`
  width: min(${props => props.$width ? `${props.$width}px` : props.$size === "wide" ? "1400px" : props.$size === "large" ? "920px" : props.$size === "compact" ? "440px" : "720px"}, 100%);
  height: ${({ $height }) => $height ? `min(${$height}px, calc(100dvh - 64px))` : 'auto'};
  max-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--admin-theme-surface, #ffffff);
  color: var(--admin-theme-text, #24212d);
  border-radius: 20px;
  box-shadow: 0 24px 64px rgba(26, 19, 51, 0.24);
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 3px #8b6cf2, 0 24px 64px rgba(26, 19, 51, 0.24);
  }

  @media (max-width: 720px) {
    max-height: calc(100dvh - 24px);
    border-radius: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 28px 20px;
  border-bottom: 1px solid var(--admin-theme-border, #ebe8f1);
`;

const HeadingGroup = styled.div`
  min-width: 0;

  h2 {
    margin: 0;
    ${fontStyles.Bold}
    font-size: 22px;
    line-height: 1.4;
  }

  p {
    margin: 6px 0 0;
    color: var(--admin-theme-muted, #696474);
    font-size: 14px;
    line-height: 1.5;
  }
`;

const CloseButton = styled(ModalCloseButton)`margin: -8px -8px 0 0;`;

const Body = styled.div<{ $scroll: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: ${({ $scroll }) => $scroll ? 'auto' : 'hidden'};
  display: ${({ $scroll }) => $scroll ? 'block' : 'flex'};
  flex-direction: column;
  padding: 24px 28px 28px;
  overscroll-behavior: contain;

  @media (max-width: 720px) {
    padding: 20px;
  }
`;

const Footer = styled.footer`
  flex: 0 0 auto; display: flex; justify-content: flex-end; gap: var(--space-3);
  padding: var(--space-4); border-top: 1px solid var(--color-border-default); background: var(--color-bg-surface);
`;
